/**
 * Pure white-label add-on helpers. No React, Firebase, or i18n imports so
 * node:test can load this file. Prices always come from the caller (the
 * status payload). This module never invents an amount.
 */

export const WHITE_LABEL_ADDON_KEY = "whiteLabel";
export const WHITE_LABEL_TIER_ID = "white_label";

/** Tiered paywalls (automations, templates, competitors). White-label stays out. */
export const TIERED_PAYWALL_ADDON_KEYS = Object.freeze([
  "automations",
  "templates",
  "competitors",
]);

export const WHITE_LABEL_STATUS = Object.freeze({
  PENDING_BRANDING: "pendingBranding",
  CONFIGURING: "configuring",
  ACTIVE: "active",
  REVERT_PENDING: "revertPending",
  REVERTED: "reverted",
});

export const WHITE_LABEL_HIDDEN_REASONS = Object.freeze([
  "enterprise",
  "white_label_disabled",
  "plan_not_eligible",
]);

const ENTERPRISE_PLAN = "enterprise";
const SELF_SERVE_PLANS = Object.freeze([
  "basic",
  "starter",
  "growth",
  "pro",
  "advanced",
  "studio",
  "agency",
]);
const APP_SUMO_PLANS = Object.freeze(["basic", "starter", "pro"]);

const paidPriceMonthly = (config) => {
  if (config?.enabled !== true) return null;
  const tier = (config.tiers || []).find(
    (item) => item?.id === WHITE_LABEL_TIER_ID && Number(item.priceMonthly) > 0
  );
  return tier ? Number(tier.priceMonthly) : null;
};

const planIsEligible = ({ eligiblePlans, subscriptionPlan }) => {
  if (Array.isArray(eligiblePlans))
    return eligiblePlans.includes(subscriptionPlan);
  if (eligiblePlans != null) return false;
  return SELF_SERVE_PLANS.includes(subscriptionPlan);
};

/**
 * Same order as the server evaluator. The AppSumo license row is not read
 * here: an AppSumo agency can buy when its ads plan is active and maps to
 * basic, starter, or pro.
 */
export const evaluateWhiteLabelEligibility = ({
  config = null,
  advertiser = null,
  isAppSumo = false,
  themeExists = false,
  setup = null,
  agencyAddon = null,
} = {}) => {
  const subscriptionID = agencyAddon?.stripeSubscriptionID;
  if (typeof subscriptionID === "string" && subscriptionID.trim()) {
    return {
      eligible: true,
      canBuy: false,
      grandfathered: false,
      reason: "subscribed",
    };
  }

  if (themeExists === true && !setup?.purchasedAt) {
    return {
      eligible: false,
      canBuy: false,
      grandfathered: true,
      reason: "grandfathered",
    };
  }

  const planInfo = advertiser?.subscriptionPlanInfo || null;
  const subscriptionPlan = advertiser?.subscriptionPlan || null;
  if (planInfo?.isCustom === true || subscriptionPlan === ENTERPRISE_PLAN) {
    return {
      eligible: false,
      canBuy: false,
      grandfathered: false,
      reason: "enterprise",
    };
  }

  const hasPaidTier = (config?.tiers || []).some(
    (tier) => tier?.id === WHITE_LABEL_TIER_ID && Number(tier.priceMonthly) > 0
  );
  if (config?.enabled !== true || !hasPaidTier) {
    return {
      eligible: false,
      canBuy: false,
      grandfathered: false,
      reason: "white_label_disabled",
    };
  }

  if (isAppSumo === true) {
    if (
      advertiser?.isPlanActive === true &&
      APP_SUMO_PLANS.includes(subscriptionPlan)
    ) {
      return {
        eligible: true,
        canBuy: true,
        grandfathered: false,
        reason: "appsumo",
      };
    }
    return {
      eligible: false,
      canBuy: false,
      grandfathered: false,
      reason: "plan_inactive",
    };
  }

  if (advertiser?.isPlanActive !== true) {
    return {
      eligible: false,
      canBuy: false,
      grandfathered: false,
      reason: "plan_inactive",
    };
  }

  if (
    !planIsEligible({ eligiblePlans: config?.eligiblePlans, subscriptionPlan })
  ) {
    return {
      eligible: false,
      canBuy: false,
      grandfathered: false,
      reason: "plan_not_eligible",
    };
  }

  return {
    eligible: true,
    canBuy: true,
    grandfathered: false,
    reason: "self_serve",
  };
};

export const buildWhiteLabelClientStatus = ({
  catalogEntry = null,
  agencyAddon = null,
  setup = null,
  advertiser = null,
  isAppSumo = false,
  themeExists = false,
  effective = null,
  tiers = [],
} = {}) => {
  const config = {
    enabled: catalogEntry?.enabled === true,
    eligiblePlans: Array.isArray(catalogEntry?.eligiblePlans)
      ? catalogEntry.eligiblePlans
      : catalogEntry?.eligiblePlans == null
        ? null
        : [],
    tiers: Array.isArray(catalogEntry?.tiers) ? catalogEntry.tiers : [],
  };
  const eligibility = evaluateWhiteLabelEligibility({
    config,
    advertiser,
    isAppSumo,
    themeExists,
    setup,
    agencyAddon,
  });
  let status = null;
  if (eligibility.grandfathered) status = WHITE_LABEL_STATUS.ACTIVE;
  else if (setup?.status) status = setup.status;

  return {
    ok: true,
    eligible: eligibility.eligible,
    ungated: false,
    effective,
    tiers,
    agencyAddon: agencyAddon || null,
    whiteLabel: {
      canBuy: eligibility.canBuy,
      reason: eligibility.reason,
      grandfathered: eligibility.grandfathered,
      priceMonthly: paidPriceMonthly(config),
      currency: "USD",
      isAppSumo: isAppSumo === true,
      status,
      branding: setup?.branding || null,
      submittedAt: setup?.submittedAt ?? null,
      renewDate: agencyAddon?.renewDate ?? null,
      cancelDateTime: agencyAddon?.cancelDateTime ?? null,
      billingStatus: agencyAddon?.billingStatus ?? null,
    },
  };
};

export const WHITE_LABEL_CHECKOUT_POLL_MS = 2000;
export const WHITE_LABEL_CHECKOUT_TIMEOUT_MS = 30000;

const ADVERTISER_ROLE = "advertiser";
const SUPER_ADMIN_ROLE = "superAdmin";

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const HOST_LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
const URL_MAX_LENGTH = 500;
const NOTES_MAX_LENGTH = 500;
const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 60;
const DOMAIN_MAX_LENGTH = 253;
const BLOCKED_DOMAIN = "adle.ai";

const INVALID_BRANDING = "whiteLabelInvalidBranding";

const brandingFieldErrors = (fieldErrors) => ({
  ok: false,
  code: INVALID_BRANDING,
  fieldErrors,
  field: Object.keys(fieldErrors)[0] || null,
});

const normalizeRequiredName = (raw) => {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (value.length < NAME_MIN_LENGTH || value.length > NAME_MAX_LENGTH) {
    return null;
  }
  return value;
};

const normalizeHttpsUrl = (raw, { required }) => {
  if (raw == null || raw === "") {
    return required ? null : "";
  }
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value) return required ? null : "";
  if (value.length > URL_MAX_LENGTH) return null;
  if (!value.startsWith("https://")) return null;
  let parsed;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;
  return value;
};

const normalizeColor = (raw, { required }) => {
  if (raw == null || (typeof raw === "string" && raw.trim() === "")) {
    return required ? null : "";
  }
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!HEX_COLOR.test(value)) return null;
  return value.toUpperCase();
};

const extractDomainHost = (raw) => {
  if (typeof raw !== "string") return null;
  let value = raw.trim();
  if (!value) return null;

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      return parsed.hostname.toLowerCase();
    } catch {
      return null;
    }
  }

  value = value.replace(/\/+$/, "");
  value = value.split(/[/?#]/)[0];
  const bracketHost = value.match(/^\[([^\]]+)\](?::\d+)?$/);
  if (bracketHost) return bracketHost[1].toLowerCase();
  const parts = value.split(":");
  if (parts.length === 2 && /^\d+$/.test(parts[1])) {
    value = parts[0];
  }
  return value.toLowerCase();
};

const normalizeDomain = (raw) => {
  const value = extractDomainHost(raw);
  if (!value || value.length > DOMAIN_MAX_LENGTH) return null;
  if (/\s/.test(value)) return null;
  if (value.includes("..")) return null;
  const labels = value.split(".");
  if (labels.length < 2) return null;
  if (!labels.every((label) => HOST_LABEL.test(label))) return null;
  const tld = labels[labels.length - 1];
  if (tld.length < 2) return null;
  if (value === BLOCKED_DOMAIN || value.endsWith(`.${BLOCKED_DOMAIN}`)) {
    return null;
  }
  return value;
};

const domainValidationReason = (raw) => {
  if (typeof raw !== "string" || !raw.trim()) return "required";
  const host = extractDomainHost(raw);
  if (
    host === BLOCKED_DOMAIN ||
    (typeof host === "string" && host.endsWith(`.${BLOCKED_DOMAIN}`))
  ) {
    return "blockedDomain";
  }
  return "invalidDomain";
};

const logoValidationReason = (raw, { required }) => {
  if (raw == null || (typeof raw === "string" && !raw.trim())) {
    return required ? "required" : null;
  }
  return normalizeHttpsUrl(raw, { required }) ? null : "invalidFile";
};

const colorValidationReason = (raw, { required }) => {
  if (raw == null || (typeof raw === "string" && !raw.trim())) {
    return required ? "required" : null;
  }
  return normalizeColor(raw, { required }) ? null : "invalidColor";
};

const nameValidationReason = (raw) => {
  if (typeof raw !== "string" || !raw.trim()) return "required";
  return normalizeRequiredName(raw) ? null : "invalidName";
};

const normalizeNotes = (raw) => {
  if (raw == null || raw === "") return "";
  if (typeof raw !== "string") return null;
  if (raw.length > NOTES_MAX_LENGTH) return null;
  return raw;
};

/**
 * Mirrors the branding endpoint. Returns every invalid field at once.
 * Bucket ownership is checked on the server; the client only checks https.
 */
export const validateWhiteLabelBranding = (body = {}) => {
  const fieldErrors = {};

  const nameBusiness = normalizeRequiredName(body.nameBusiness);
  const nameReason = nameValidationReason(body.nameBusiness);
  if (nameReason) fieldErrors.nameBusiness = nameReason;

  const logo = normalizeHttpsUrl(body.logo, { required: true });
  const logoReason = logoValidationReason(body.logo, { required: true });
  if (logoReason) fieldErrors.logo = logoReason;

  const optionalLogos = {};
  for (const field of ["logoNavbar", "logoLogin", "favicon"]) {
    const url = normalizeHttpsUrl(body[field], { required: false });
    const reason = logoValidationReason(body[field], { required: false });
    if (reason) fieldErrors[field] = reason;
    else optionalLogos[field] = url || null;
  }

  const primaryColor = normalizeColor(body.primaryColor, { required: true });
  const primaryReason = colorValidationReason(body.primaryColor, {
    required: true,
  });
  if (primaryReason) fieldErrors.primaryColor = primaryReason;

  const secondaryColor = normalizeColor(body.secondaryColor, {
    required: true,
  });
  const secondaryReason = colorValidationReason(body.secondaryColor, {
    required: true,
  });
  if (secondaryReason) fieldErrors.secondaryColor = secondaryReason;

  const primaryButtonColor = normalizeColor(body.primaryButtonColor, {
    required: false,
  });
  const buttonReason = colorValidationReason(body.primaryButtonColor, {
    required: false,
  });
  if (buttonReason) fieldErrors.primaryButtonColor = buttonReason;

  const desiredDomain = normalizeDomain(body.desiredDomain);
  if (!desiredDomain) {
    fieldErrors.desiredDomain = domainValidationReason(body.desiredDomain);
  }

  const notes = normalizeNotes(body.notes);
  if (notes == null) fieldErrors.notes = "invalidNotes";

  if (Object.keys(fieldErrors).length > 0) {
    return brandingFieldErrors(fieldErrors);
  }

  return {
    ok: true,
    branding: {
      nameBusiness,
      logo,
      logoNavbar: optionalLogos.logoNavbar,
      logoLogin: optionalLogos.logoLogin,
      favicon: optionalLogos.favicon,
      primaryColor,
      secondaryColor,
      primaryButtonColor: primaryButtonColor || null,
      desiredDomain,
      notes,
    },
  };
};

export const EMPTY_WHITE_LABEL_BRANDING_FORM = Object.freeze({
  nameBusiness: "",
  logo: "",
  logoNavbar: "",
  logoLogin: "",
  favicon: "",
  primaryColor: "",
  secondaryColor: "",
  primaryButtonColor: "",
  desiredDomain: "",
  notes: "",
});

export const brandingToFormValues = (branding) => {
  const source = branding && typeof branding === "object" ? branding : {};
  return {
    nameBusiness: source.nameBusiness || "",
    logo: source.logo || "",
    logoNavbar: source.logoNavbar || "",
    logoLogin: source.logoLogin || "",
    favicon: source.favicon || "",
    primaryColor: source.primaryColor || "",
    secondaryColor: source.secondaryColor || "",
    primaryButtonColor: source.primaryButtonColor || "",
    desiredDomain: source.desiredDomain || "",
    notes: source.notes || "",
  };
};

export const formatWhiteLabelMonthlyPrice = ({
  priceMonthly,
  currency,
  locale,
} = {}) => {
  const amount = Number(priceMonthly);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const code =
    typeof currency === "string" && currency.trim()
      ? currency.trim().toUpperCase()
      : "";
  if (!code) return null;
  try {
    return new Intl.NumberFormat(locale || undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    return null;
  }
};

export const whiteLabelSubscriptionId = (status) => {
  const id = status?.agencyAddon?.stripeSubscriptionID;
  return typeof id === "string" && id.trim() ? id.trim() : "";
};

export const shouldShowWhiteLabelBillingRow = (status) => {
  if (!status || status.ok === false) return false;
  const whiteLabel = status.whiteLabel;
  if (!whiteLabel || typeof whiteLabel !== "object") return false;
  const hidden = WHITE_LABEL_HIDDEN_REASONS.includes(whiteLabel.reason);
  if (hidden && !whiteLabelSubscriptionId(status)) return false;
  return true;
};

export const canManageAgencyBilling = ({
  role,
  agencyID,
  agenciesAvailable,
} = {}) => {
  if (role === ADVERTISER_ROLE) return true;
  if (role !== SUPER_ADMIN_ROLE) return false;
  if (!agencyID || !Array.isArray(agenciesAvailable)) return false;
  return agenciesAvailable.includes(agencyID);
};

const hasDisplayablePrice = (whiteLabel) =>
  formatWhiteLabelMonthlyPrice({
    priceMonthly: whiteLabel?.priceMonthly,
    currency: whiteLabel?.currency,
  }) != null;

export const getWhiteLabelPricingBenefit = ({
  isSignedIn,
  whiteLabel,
} = {}) => {
  if (!isSignedIn || !whiteLabel || typeof whiteLabel !== "object") {
    return "hidden";
  }
  if (whiteLabel.grandfathered === true) return "alreadyActive";
  if (whiteLabel.status && whiteLabel.status !== WHITE_LABEL_STATUS.REVERTED) {
    return "alreadyActive";
  }
  if (whiteLabel.canBuy === true && hasDisplayablePrice(whiteLabel)) {
    return "buy";
  }
  return "hidden";
};

export const getWhiteLabelBillingView = ({ status, canManage } = {}) => {
  const whiteLabel = status?.whiteLabel || {};
  const grandfathered = whiteLabel.grandfathered === true;
  const setupStatus = whiteLabel.status || null;
  const unpaid =
    status?.agencyAddon?.billingStatus === "unpaid" ||
    whiteLabel.billingStatus === "unpaid";
  const canBuy =
    whiteLabel.canBuy === true &&
    !grandfathered &&
    hasDisplayablePrice(whiteLabel);
  // The branding endpoint rejects unpaid add-ons (not active).
  const showCompleteBrand =
    !grandfathered &&
    !unpaid &&
    setupStatus === WHITE_LABEL_STATUS.PENDING_BRANDING;
  const showEditBrand =
    !grandfathered &&
    !unpaid &&
    (setupStatus === WHITE_LABEL_STATUS.CONFIGURING ||
      setupStatus === WHITE_LABEL_STATUS.ACTIVE);

  return {
    canBuy,
    buyDisabled: canBuy && canManage !== true,
    brandDisabled: (showCompleteBrand || showEditBrand) && canManage !== true,
    showCompleteBrand,
    showEditBrand,
    showSubscriptionMenu: Boolean(whiteLabelSubscriptionId(status)),
    unpaid,
    grandfathered,
    setupStatus,
    cancelDateTime:
      whiteLabel.cancelDateTime ?? status?.agencyAddon?.cancelDateTime ?? null,
    renewDate: whiteLabel.renewDate ?? status?.agencyAddon?.renewDate ?? null,
    priceMonthly: whiteLabel.priceMonthly,
    currency: whiteLabel.currency,
  };
};

export const shouldOpenWhiteLabelBranding = (status) => {
  if (!status || status.ok === false) return false;
  if (status.whiteLabel?.grandfathered === true) return false;
  return status.agencyAddon?.isActive === true;
};

export const readWhiteLabelCheckoutResult = (params) => {
  if (!params || typeof params.get !== "function") return null;
  if (params.get("featureAddonPaymentMethod")) return null;
  if (params.get("addonKey") !== WHITE_LABEL_ADDON_KEY) return null;
  const featureAddon = params.get("featureAddon");
  if (featureAddon === "success" || featureAddon === "cancel") {
    return featureAddon;
  }
  return null;
};

export const stripWhiteLabelCheckoutParams = (params) => {
  const next = new URLSearchParams(params);
  next.delete("featureAddon");
  next.delete("addonKey");
  return next;
};

/** Arguments after `error` for LaunchError on feature-addon actions. */
export const buildFeatureAddonLaunchErrorArgs = ({
  functionName,
  showAlert = true,
} = {}) => [
  showAlert,
  [],
  {
    functionName,
    tags: { action: "featureAddons" },
    level: "error",
  },
];
